import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { SITE_URL } from "@/lib/locale";
import { jsonLdScript, breadcrumbSchema } from "@/lib/seo";

const TITLE = "Calvarius tools — Methodical instruments for economic market analysis";
const DESC =
  "Free tools for performance marketing, SEO, and web performance. Lighthouse live check, profitability calculator, and more — substantive, no registration required.";
const URL_ = `${SITE_URL}/en/tools`;
const ALT = `${SITE_URL}/tools`;

export const Route = createFileRoute("/en/tools/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL_ },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL_ },
      { rel: "alternate", hrefLang: "en", href: URL_ },
      { rel: "alternate", hrefLang: "de", href: ALT },
      { rel: "alternate", hrefLang: "x-default", href: ALT },
    ],
    scripts: [
      jsonLdScript(
        breadcrumbSchema([
          { name: "Start", path: "/en" },
          { name: "Tools", path: "/en/tools" },
        ]),
      ),
    ],
  }),
  component: ToolsHubEN,
});

function ToolsHubEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="CALVARIUS TOOLS"
          headline={<>Tools for <em>economic</em> market analysis</>}
          lead="Publicly available analysis tools for performance marketing, SEO, and web performance. Free, no registration."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Tools" }]}
        />

        <Section number="02" kicker="Tools" headline={<>Three tools, <em>live in production.</em></>} variant="tinted">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/en/tools/lighthouse-check"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Lighthouse live check</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Real Lighthouse audit of your website in under a minute. Four dimensions, Core Web Vitals, plus three concrete improvement levers in our methodical assessment.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Open tool →
              </span>
            </Link>

            <Link
              to="/en/tools/profitability-calculator"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Performance marketing profitability calculator</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Calculates the economically viable CPC thresholds for performance marketing from order value, margin, and conversion rate — plus industry reality check.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Open tool →
              </span>
            </Link>

            <Link
              to="/en/tools/performance-marketing-checklist"
              className="group flex h-full flex-col gap-5 rounded-lg border border-rule bg-white p-8 transition-all hover:-translate-y-0.5 hover:border-cyan hover:shadow-[0_12px_40px_-20px_oklch(0.6_0.11_225/0.4)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Live · PDF</div>
              <h3 className="font-display text-2xl text-ink leading-tight">Performance marketing checklist for SMBs</h3>
              <p className="text-[15px] leading-relaxed text-ink-2">
                Methodical setup sequence in seven phases. PDF download.
              </p>
              <span className="mt-auto pt-4 font-mono text-xs uppercase tracking-wider text-ink-2 transition-colors group-hover:text-cyan-deep">
                Open checklist →
              </span>
            </Link>
          </div>
        </Section>

        <Section number="03" kicker="Outlook" headline={<>More tools <em>in preparation.</em></>}>
          <div className="max-w-2xl">
            <p className="mb-6 text-[17px] leading-relaxed text-ink-2">
              More Calvarius tools are in preparation:
            </p>
            <ul className="space-y-3 text-[15px] text-ink-2">
              <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span>AI-SEO visibility check for ChatGPT, Perplexity, Claude</li>
              <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span>Further tools derived from concrete client needs</li>
            </ul>
          </div>
        </Section>

        <Section number="04" kicker="Note" headline={<>Looking for <em>Calvarius products?</em></>}>
          <div className="max-w-2xl">
            <p className="text-[17px] leading-relaxed text-ink-2">
              Calvarius also operates proprietary software products for mandate clients — <strong className="font-semibold text-ink">Delta</strong>, <strong className="font-semibold text-ink">picazzoo</strong>, and <strong className="font-semibold text-ink">GonzalAds</strong>. These are documented separately under{" "}
              <Link to="/en/services/tools" className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                Calvarius products
              </Link>.
            </p>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
